import { Button } from '@creditornot/cb-button';
import React, { useState } from 'react';
import { Form } from 'react-final-form';
import { FormattedMessage } from 'react-intl';

import { CampaignType } from 'apis/restaurant-api/merchant-admin/campaigns/types';
import { WoltModal } from 'common/components';
import { convertMonetaryIntToDecimal } from 'common/utils/currencyDecimals';
import { useCampaignModuleContext } from 'modules/campaigns/context';
import { CampaignRulesFormValues } from 'modules/campaigns/types';

import RulesDetails from './RulesDetails';
import RulesFormModal from './RulesFormModal';
import { shouldPreventChangingMinimalBasketRequirement } from './utils';
import { CampaignsTargetContent } from '../../utils/telemetry/constants';
import {
  telemetryNewCampaignInteractionClickCampaignRules,
  telemetryUpdateCampaignInteractionClickCampaignUpdate,
} from '../../utils/telemetry/telemetry';
import UnsavedChangesModal from '../modals/UnsavedChangesModal/UnsavedChangesModal';

export type RulesFormItemProps = {
  minBasketSizeOptions?: number[];
  initialValues: CampaignRulesFormValues;
  onUpdate: (values: CampaignRulesFormValues) => void;
  campaignType: CampaignType;
  isExistingRunningCampaign: boolean;
  isNewCampaignRoute?: boolean;
  isChangeButtonHidden?: boolean;
  'data-testid'?: string;
};

const RulesFormItem = ({
  minBasketSizeOptions,
  onUpdate,
  initialValues,
  isExistingRunningCampaign,
  isNewCampaignRoute = false,
  campaignType,
  isChangeButtonHidden,
  ...props
}: RulesFormItemProps) => {
  const { currency, country } = useCampaignModuleContext();
  const preventChangingMinimalBasketRequirement = shouldPreventChangingMinimalBasketRequirement(country, campaignType);
  const [isUnsavedChangesModalOpen, setIsUnsavedChangesModalOpen] = useState<boolean>(false);
  const [isRulesModalOpen, setIsRulesModalOpen] = useState(false);

  const handleCloseModals = (isFormDirty?: boolean) => {
    if (isFormDirty) {
      setIsUnsavedChangesModalOpen(true);
    } else {
      setIsUnsavedChangesModalOpen(false);
      setIsRulesModalOpen(false);
    }
  };

  const handleRulesUpdate = (values: CampaignRulesFormValues) => {
    onUpdate(values);

    handleCloseModals();
  };

  const handleChangeRulesClick = () => {
    if (isNewCampaignRoute) {
      telemetryNewCampaignInteractionClickCampaignRules(CampaignsTargetContent.NEW.CHANGE_RULES, campaignType);
    } else {
      telemetryUpdateCampaignInteractionClickCampaignUpdate(CampaignsTargetContent.EDIT.CHANGE_RULES, campaignType);
    }
    setIsRulesModalOpen(true);
  };

  const nonNullSortedMinBasketSizeOptions = minBasketSizeOptions?.filter(option => option !== 0).sort((a, b) => a - b);
  const smallestMinBasketSizeOption =
    nonNullSortedMinBasketSizeOptions && nonNullSortedMinBasketSizeOptions.length > 0
      ? nonNullSortedMinBasketSizeOptions[0]
      : undefined;
  return (
    <>
      <Form<CampaignRulesFormValues>
        onSubmit={handleRulesUpdate}
        initialValues={{
          ...initialValues,
          isMinBasketIncreased: preventChangingMinimalBasketRequirement ? true : initialValues.isMinBasketIncreased,
          minBasketSize:
            preventChangingMinimalBasketRequirement && smallestMinBasketSizeOption
              ? smallestMinBasketSizeOption
              : initialValues.minBasketSize,
        }}
      >
        {({ handleSubmit, dirty, form }) => (
          <WoltModal isOpen={isRulesModalOpen} onRequestClose={() => handleCloseModals(dirty)}>
            <RulesFormModal
              onClose={() => handleCloseModals(dirty)}
              onUpdate={handleSubmit}
              minBasketSizeOptions={minBasketSizeOptions}
              campaignType={campaignType}
              isNewCampaignRoute={isNewCampaignRoute}
            />
            <UnsavedChangesModal
              isOpen={dirty && isUnsavedChangesModalOpen}
              onDropChanges={() => {
                handleCloseModals();
                form.reset();
              }}
              onClose={() => setIsUnsavedChangesModalOpen(false)}
            />
          </WoltModal>
        )}
      </Form>
      <RulesDetails
        data-testid={props['data-testid']}
        action={
          isChangeButtonHidden ? null : (
            <Button
              variant="lightBlue"
              size="small"
              onClick={handleChangeRulesClick}
              disabled={isExistingRunningCampaign}
            >
              <FormattedMessage id="campaigns.form.rules.change-rules" />
            </Button>
          )
        }
        minBasketSize={
          initialValues.minBasketSize !== undefined
            ? convertMonetaryIntToDecimal(Number(initialValues.minBasketSize), currency)
            : undefined
        }
        firstPurchaseOnly={initialValues.firstPurchaseOnly}
        usageType={initialValues.perUserUsageLimit ? 'limited' : 'indefinitely'}
        perUserUsageLimit={Number(initialValues?.perUserUsageLimit) || 0}
      />
    </>
  );
};

export default RulesFormItem;
